import React from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { graphql, Link } from 'gatsby';

import { useContactSalesData } from './hooks';
import HorizontalArrow from './assets/horizontalArrow.inline.svg';

import { filterProps } from 'utils/helpers';
import { RichText } from 'components/Prismic';
import { ArrayElement, Normalize } from 'utils/types';

export type TContactSales = ArrayElement<
  Normalize<Queries.ContactSalesDataFragment['data']['content']>
>;

const Container = styled('div', filterProps('hasTopPadding', 'isDark'))<{
  hasTopPadding: boolean;
  isDark: boolean;
}>`
  ${({ theme: { colors }, hasTopPadding, isDark }) => css`
    display: grid;
    grid-auto-flow: column;
    column-gap: 8.5rem;
    justify-content: center;
    background-color: ${isDark ? colors.backgroundDark : colors.offWhite};
    padding: ${hasTopPadding ? '23rem' : 0} 0 19.75rem;
  `}
`;

const Tab = styled.div`
  box-sizing: border-box;
  display: grid;
  row-gap: 6rem;
  align-items: center;
  border-radius: 2rem;
  background-color: #343434;
  padding: 6.25rem 3.75rem 6.75rem;
  width: 69.875rem;
`;

const Title = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h4 {
      ${tp.useFont(fonts => fonts.Switzer.light)};

      text-align: center;
      line-height: 1.47;
      color: ${colors.white};
      font-size: 4.5rem;
    }
  `}
`;

const Content = styled(RichText)`
  ${({ theme: { tp } }) => css`
    display: flex;
    flex-direction: column;

    & ul {
      ${tp.useFont(fonts => fonts.Switzer.light)};

      padding-left: 3.75rem;
      line-height: 1.04;
      color: #c9c9c9fa;
      font-size: 2.75rem;
    }
  `}
`;

const arrowAnimation = keyframes`
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(1.25rem) ;
  }

`;

const Button = styled(Link)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    column-gap: 1rem;
    align-items: center;
    align-self: flex-end;
    justify-self: center;
    margin-top: 3rem;
    border-radius: 2rem;
    background-color: ${colors.primary};
    padding: 2.1875rem 4.6875rem;
    text-decoration: none;
    line-height: 1.39;
    color: ${colors.black};
    font-size: 2.25rem;

    ${tp.useFont(fonts => fonts['Nunito Sans'].bold)};

    & > svg {
      width: 4rem;
      height: 4rem;
      animation: ${arrowAnimation} 2s ease-in-out infinite alternate;
    }
  `}
`;

const ContactSales: React.FC<TContactSales> = sliceData => {
  const props = useContactSalesData(sliceData);
  if (!props) return null;

  const { isDark, hasTopPadding, tabs } = props;

  return (
    <Container isDark={isDark} hasTopPadding={hasTopPadding}>
      {tabs.map(({ title, content, link, linkLabel }, index) => (
        <Tab key={`${title}${index}`}>
          <Title render={title} />
          <Content render={content} />
          <Button to={link}>
            {linkLabel}
            <HorizontalArrow />
          </Button>
        </Tab>
      ))}
    </Container>
  );
};

export const contentContactSalesFragment = graphql`
  fragment ContactSalesData on ContactSalesEnabledPage {
    ... on PrismicSolution {
      data {
        content {
          ... on PrismicSolutionDataContentContactSales {
            slice_type

            primary {
              contact_sales_is_dark
              contact_sales_has_top_padding
            }

            items {
              contact_sales_tab_title {
                richText
              }
              contact_sales_tab_content {
                richText
              }
              contact_sales_tab_link {
                url
              }
              contact_sales_tab_link_label
            }
          }
        }
      }
    }
    ... on PrismicIndustry {
      data {
        content {
          ... on PrismicIndustryDataContentContactSales {
            slice_type

            primary {
              contact_sales_is_dark
              contact_sales_has_top_padding
            }

            items {
              contact_sales_tab_title {
                richText
              }
              contact_sales_tab_content {
                richText
              }
              contact_sales_tab_link {
                url
              }
              contact_sales_tab_link_label
            }
          }
        }
      }
    }
  }
`;

export default ContactSales;
